import { useState } from "react";
import { Upload, Loader2, Save, Camera } from "lucide-react";
import { api, compressImage, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { ZoomablePreview } from "@/components/app/ZoomablePreview";

const PhotoID = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setPreview(URL.createObjectURL(f));
  };

  const analyze = async (fileArg) => {
    const f = fileArg || file;
    if (!f) return;
    setLoading(true);
    setResult(null);
    try {
      const { base64, mime } = await compressImage(f, { maxDim: 1400, quality: 0.82 });
      const { data } = await api.post("/vehicles/identify-photo", {
        image_base64: base64, mime_type: mime,
      });
      setResult({ ...data, image_base64: base64, mime });
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onCropPhoto = async (f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    await analyze(f);
  };

  const save = async () => {
    if (!result?.identification) return;
    setSaving(true);
    const id = result.identification;
    try {
      await api.post("/vehicles/garage", {
        year: id.year_range || "",
        make: id.make || "",
        model: id.model || "",
        trim: id.trim || "",
        photo_base64: result.image_base64,
        photo_mime: result.mime,
        photo_identified: id,
      });
      toast.success("Filed in your garage");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const id = result?.identification || {};

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Station 01</p>
        <h1 className="font-display text-5xl uppercase text-cream mt-1">Photo Identification</h1>
        <p className="text-secondary mt-2 max-w-2xl">Upload a photo of a classic vehicle. Our AI historian reviews it and identifies year, make, model, trim, and body style.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel p-6">
          <span className="tag-chip">Input</span>
          <label data-testid="photo-file-dropzone" className="block mt-4 border-2 border-dashed border-shop-strong hover:border-[var(--accent-amber)] transition-colors cursor-pointer p-8 text-center">
            <input data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
            {preview ? (
              <ZoomablePreview
                src={preview}
                alt="preview"
                testid="photo-id-preview"
                className="max-h-72"
                onCrop={onCropPhoto}
                cropLabel="Re-analyze this crop"
              />
            ) : (
              <div className="text-secondary">
                <Upload size={36} className="mx-auto text-amber-brass mb-3" />
                <p className="font-display uppercase tracking-widest text-lg text-cream">Drop / Pick a photo</p>
                <p className="text-xs font-mono mt-1">JPG / PNG / WEBP</p>
              </div>
            )}
          </label>
          <button
            data-testid="photo-analyze-btn"
            onClick={() => analyze()}
            disabled={!file || loading}
            className="chrome-btn w-full mt-5 flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 size={16} className="animate-spin" /> Analyzing…</> : <><Camera size={16} /> Identify Vehicle</>}
          </button>
        </div>

        <div className="panel p-6 min-h-[400px]">
          <div className="flex items-center justify-between">
            <span className="tag-chip">Output</span>
            {result && id.confidence && (
              <span className="font-mono text-xs uppercase text-amber-brass">Confidence: {id.confidence}</span>
            )}
          </div>

          {!result && !loading && (
            <div className="text-secondary text-sm mt-6">The identified vehicle report will appear here.</div>
          )}

          {loading && (
            <div className="flex items-center gap-2 mt-6 text-amber-brass font-mono text-sm">
              <Loader2 size={16} className="animate-spin" /> Consulting the historian…
            </div>
          )}

          {result && (
            <div data-testid="photo-result" className="mt-5 space-y-4">
              <div>
                <div className="font-display text-4xl uppercase text-cream leading-tight">
                  {id.year_range} {id.make}
                </div>
                <div className="font-display text-2xl uppercase text-red-candy">
                  {id.model} {id.trim && <span className="text-amber-brass">— {id.trim}</span>}
                </div>
                <div className="font-mono text-xs text-secondary mt-1">{id.body_style}</div>
              </div>

              {id.special_variant && (
                <div className="border-l-4 border-[var(--accent-red)] bg-[rgba(217,32,39,0.08)] px-4 py-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-candy">Special Variant</div>
                  <div className="font-display text-2xl uppercase text-cream mt-0.5">{id.special_variant}</div>
                </div>
              )}

              {id.identifying_features?.length > 0 && (
                <div>
                  <div className="font-mono text-xs uppercase text-amber-brass mb-2">Identifying Features</div>
                  <ul className="text-sm text-cream list-none space-y-1">
                    {id.identifying_features.map((f, i) => (
                      <li key={i}><span className="text-amber-brass font-mono">→</span> {f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {id.notable_specs && (
                <div>
                  <div className="font-mono text-xs uppercase text-amber-brass mb-1">Notable Specs</div>
                  <p className="text-sm text-cream">{id.notable_specs}</p>
                </div>
              )}
              {id.historical_notes && (
                <div>
                  <div className="font-mono text-xs uppercase text-amber-brass mb-1">Historical Notes</div>
                  <p className="text-sm text-cream">{id.historical_notes}</p>
                </div>
              )}
              {id.notes && <p className="text-xs italic text-secondary">{id.notes}</p>}

              <button data-testid="photo-save-btn" onClick={save} disabled={saving} className="ghost-btn flex items-center gap-2">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save to Garage
              </button>

              {result.raw && !id.year_range && (
                <pre className="text-xs bg-elevated p-3 border border-shop text-secondary whitespace-pre-wrap">{result.raw}</pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoID;
